import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Box, useTheme, styled } from '@mui/material'
import moment from 'moment'
import ComputerConnectBtn from 'components/DynamicComponents/ComputerConnectButton'
import { PasswordModal } from 'components/PasswordModal'
import MasterLockOverlay from 'packages/eid-ui/MasterLockOverlay'
import { useAppState } from 'appContext'
import { useTranslation } from 'react-i18next'
import ComputersConnectedWhen from 'components/DynamicComponents/ComputersConnectedWhen'
import CredentialsLockedAttribute from 'components/DynamicComponents/CredentialsLockedAttribute'
import CredentialStatusButton from 'components/DynamicComponents/CredentialStatusButton'
import CredentialRemainingTime from 'components/DynamicComponents/CredentialRemainingTime'
import DynamicComponents from 'components/DynamicComponents'

const CardRoot = styled(Box)({
    display: 'block',
    alignItems: 'center',
    marginBottom: '0.5rem',
    '& h6': {
        display: 'block',
    },
    '& p': {
        padding: '0 !important',
        '& button': {
            width: '11.5rem',
            textAlign: 'center',
        },
    },
    '& p p': {
        padding: '0 !important',
    },
})

const MasterLockContainer = styled(Box)({
    '& >div div:nth-last-child(2)': {
        top: '-2.5rem',
    },
})

const ConnectButtonContainer = styled(Box)(({ preApproved }) => ({
    ' & > div >div': {
        maxWidth: preApproved ? '9rem !important' : '11rem !important',
    },

    ' & span svg:nth-last-child(1)': {
        marginTop: '1rem',
    },
    ' & button': {
        padding: preApproved ? '0 !important' : '0 0 0 1rem',

        ' & div': {
            maxWidth: 'auto !important',
        },
        ' & p': {
            width: preApproved ? '100%' : 'inherit',
            textAlign: preApproved ? 'center' : 'inherit',
        },
    },
}))

const Attribute = ({
    maxWidth,
    label,
    value,
    fallbackValue,
    orientation,
    labelStyles,
    valueStyles,
    type = '',
    item,
    attribute,
    data,
}) => {
    const [{ masterPassword }] = useAppState()
    const [open, setOpen] = useState('')
    const { t } = useTranslation()
    const handleMasterUnlock = () => {
        setOpen('enterPassword')
    }
    const display = orientation === 'vertical' ? 'block' : 'inline'
    const labelText = orientation === 'horizontal' ? `${label}: ` : label

    const theme = useTheme()

    const bindValue = (item, attribute) => {
        if (value) {
            if (type && type === 'datelocal') {
                return moment.utc(value).local().format('L LT')
            } else if (type && type === 'duration') {
                return (
                    <span>
                        <ComputersConnectedWhen
                            attribute={attribute}
                            data={data}
                        />
                    </span>
                )
            } else if (type && type === 'credentialLockedAttribute') {
                return (
                    <Box>
                        <CredentialsLockedAttribute
                            attribute={attribute}
                            data={data}
                        />
                    </Box>
                )
            } else if (type && type === 'credentialStatusBtn') {
                return <CredentialStatusButton data={data} />
            } else if (type && type === 'CredentialRemainingTime') {
                return <CredentialRemainingTime data={data} />
            } else if (attribute?.component) {
                const DynamicComponentToRender =
                    DynamicComponents[attribute.component.name]

                return (
                    <div>
                        <DynamicComponentToRender
                            attribute={attribute}
                            data={data}
                        />
                    </div>
                )
            } else {
                return value
            }
        } else {
            if (type && type === 'computerConnectBtn') {
                return (
                    <MasterLockContainer>
                        <MasterLockOverlay
                            btnLabel={t('Common_Unlock')}
                            showMasterLock={!masterPassword}
                            handleMasterUnlock={handleMasterUnlock}
                            styles={{
                                minHeight: '6.5rem',
                                marginLeft: '-3.2rem',
                                marginRight: '-2rem',
                                maxWidth: '20rem',
                                width: '100%',
                            }}
                        >
                            <Box
                                sx={{
                                    fontSize: '1.3rem',
                                    color: theme?.palette?.common?.black,
                                    fontWeight: 400,
                                    marginBottom: '1.6rem',
                                }}
                            >
                                <ConnectButtonContainer
                                    preApproved={item.isPreApproved}
                                >
                                    <ComputerConnectBtn
                                        parentStyle={{}}
                                        showPrefixIcon={false}
                                        data={item}
                                        drawerView={false}
                                        showCalenderIcon={true}
                                    />
                                </ConnectButtonContainer>
                            </Box>
                        </MasterLockOverlay>
                    </MasterLockContainer>
                )
            } else if (type && type === 'credentialLockedAttribute') {
                return (
                    <Box>
                        <CredentialsLockedAttribute
                            attribute={attribute}
                            data={data}
                        />
                    </Box>
                )
            } else if (type && type === 'credentialStatusBtn') {
                return <CredentialStatusButton data={data} />
            } else if (attribute?.component) {
                const DynamicComponentToRender =
                    DynamicComponents[attribute.component.name]

                return (
                    <div>
                        <DynamicComponentToRender
                            attribute={attribute}
                            data={data}
                        />
                    </div>
                )
            } else {
                return <span>{fallbackValue}</span>
            }
        }
    }

    return (
        <Box style={{ maxWidth }}>
            {!masterPassword && (
                <PasswordModal
                    showDefaultContent={false}
                    open={open}
                    setOpen={setOpen}
                />
            )}
            <CardRoot>
                <Typography
                    variant="subtitle1"
                    display={display}
                    style={{ color: '#b4b4b4', ...labelStyles }}
                >
                    {labelText}
                </Typography>
                <Typography
                    display={display}
                    style={{
                        color: theme?.palette?.common?.black,
                        wordWrap: 'break-word',
                        fontSize: '1.4rem',

                        ...valueStyles,
                    }}
                >
                    {bindValue(item, attribute)}
                </Typography>
            </CardRoot>
        </Box>
    )
}

Attribute.propTypes = {
    orientation: PropTypes.oneOf(['horizontal', 'vertical']),
}

Attribute.defaultProps = {
    maxWidth: 'fit-content',
    label: '',
    orientation: 'horizontal',
    fallbackValue: '-',
    labelStyles: {},
    valueStyles: {},
}

export default Attribute
